import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';

export type SalesStepHintTone = 'info' | 'success' | 'warning';

const TONE_COLORS: Record<SalesStepHintTone, string> = {
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main',
};

/** Dark shade for text on the soft background (readable contrast). */
const SOFT_TEXT_COLORS: Record<SalesStepHintTone, string> = {
  info: 'info.dark',
  success: 'success.dark',
  warning: 'warning.dark',
};

interface SalesStepHintProps {
  children: React.ReactNode;
  tone?: SalesStepHintTone;
  /** `plain`: icon + colored text. `soft`: same, on a light tinted background (key notices). */
  variant?: 'plain' | 'soft';
  sx?: SxProps<Theme>;
}

const SalesStepHint: React.FC<SalesStepHintProps> = ({
  children,
  tone = 'info',
  variant = 'plain',
  sx,
}) => {
  const isSoft = variant === 'soft';
  const color = isSoft ? SOFT_TEXT_COLORS[tone] : TONE_COLORS[tone];

  return (
    <Box
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mb: 2,
          ...(isSoft && {
            px: 1.5,
            py: 1,
            borderRadius: '8px',
            backgroundColor: alpha(
              theme.palette[tone].main,
              theme.palette.mode === 'dark' ? 0.16 : 0.08,
            ),
          }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color, mt: '2px', flexShrink: 0 }} />
      <Typography
        variant="body2"
        sx={(theme) => ({
          color: isSoft && theme.palette.mode === 'dark' ? theme.palette[tone].light : color,
          lineHeight: 1.5,
        })}
      >
        {children}
      </Typography>
    </Box>
  );
};

export default SalesStepHint;
